import { type AxiosResponse, type InternalAxiosRequestConfig } from 'axios';
import { camelizeKeys } from 'humps';

import { getConfig } from '@/modules/runtimeConfig';

// Apigee runs in "dual-mint" mode by default (skipLegacySession unset/false): it
// returns BOTH a JWT and a Django `sessionid` cookie, and auth-protected endpoints
// expect both. `withCredentials: true` tells the browser to attach the cookie on
// cross-origin calls.
//
// Note the absent `baseURL` — see `applyBaseUrl`.
export const axiosConfig = {
  timeout: 30_000,
  withCredentials: true,
  headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
};

// All B2B endpoints route through Apigee, the same gateway the login app uses.
// Apigee handles per-path auth (JWT for protected routes, pre-auth for
// login/signin/etc.) and includes localhost in its CORS allowlist, so the same URL
// works in `expo start --web`, native, and production builds.
//
// Resolved PER REQUEST rather than baked into `axiosConfig`, and that is load-bearing
// rather than stylistic. Both clients are constructed at module scope, which happens
// when the api module is first imported — before `OnboardingRoot` renders and therefore
// before `setRuntimeConfig` has published a consumer's `apiBaseUrl`. A `baseURL`
// captured there would be frozen to the env fallback (`undefined` in a consumer's
// bundle, so the hard-coded staging default) no matter what the consumer passed.
// Reading it in the interceptor moves the read to request time, which is always
// after the provider rendered. Same fix as `@vidahealth/login`'s `api/client.ts`.
export const applyBaseUrl = (config: InternalAxiosRequestConfig): InternalAxiosRequestConfig => {
  config.baseURL = getConfig('apiBaseUrl');
  return config;
};

// Server speaks snake_case but the codebase is camelCase end-to-end. Camelize
// every JSON response body once at the boundary so callers can author plain
// camelCase types. Request bodies stay camelCase in app code; call sites
// decamelize with humps before POST/PUT (see eligibility.check, createUser, login).
export const camelizeResponse = <T extends AxiosResponse>(response: T): T => {
  if (response.data && typeof response.data === 'object') {
    response.data = camelizeKeys(response.data) as T['data'];
  }
  return response;
};
